import { useContext, useMemo } from "react";
import { NetworkStatus } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { RESOURCE_NAME } from "common/authorization";
import { isPositiveNumber } from "common/utils/numbers";
import { useGetUserInvitesAndUsersQuery } from "../../../generated/graphql";
import { useExportUsersAndInvitesForAccountMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { AuthContext } from "../../Authorization/AuthContext";
import { Box, FlexRow } from "../../Common/__styles__/Layout";
import { Button } from "../../Common/Button";
import {
  useLegacyTableStateInURL,
  useManualPaginationConfig,
} from "../../Common/Tables/hooks";
import { DEFAULT_PAGE_SIZE } from "../../Common/Tables/utils";
import { Header, HeaderTitle } from "../__styles__/Content";
import { useNewUserInviteModal } from "../NewUserInviteModal";
import { useExportModal } from "./shared";
import { ExportButton } from "./shared";
import TeamTable from "./TeamTable";

// TODO: remove startingPage* values from props because they're only used for testing
export const Team = ({
  startingPageIndex = 0,
  startingPageSize = DEFAULT_PAGE_SIZE,
}: {
  startingPageIndex?: number;
  startingPageSize?: number;
}) => {
  const { account, authorized } = useContext(AuthContext);
  const history = useHistory();

  const canExportUsers = authorized({
    resource: RESOURCE_NAME.USER,
    permission: "export",
  });

  const canBulkInvite = authorized({
    resource: RESOURCE_NAME.USER_INVITE,
    permission: "bulkCreate",
  });

  const [initialTableState, setTableStateInURL] = useLegacyTableStateInURL({
    defaultSortingParams: [{ id: "name", desc: false }],
  });

  const manualPaginationConfig = useManualPaginationConfig({
    ...initialTableState.pagination,
    pageIndex: startingPageIndex,
    pageSize: startingPageSize,
  });

  const variables = {
    page: manualPaginationConfig.pagination.pageIndex + 1,
    pageSize: manualPaginationConfig.pagination.pageSize,
  };

  const {
    data: currentData,
    networkStatus,
    loading,
    error,
    previousData,
    refetch,
  } = useGetUserInvitesAndUsersQuery({ variables });

  const [showNewUserModal, hideNewUserModal] = useNewUserInviteModal({
    onCancel: () => {
      hideNewUserModal();
    },
    onSubmit: async () => {
      await refetch();
      hideNewUserModal();
    },
  });

  const defaultData = useMemo(() => [], []);

  const data =
    networkStatus === NetworkStatus.setVariables ? previousData : currentData;

  const showExportUsers =
    isPositiveNumber(data?.usersAndInvites.pageInfo.totalCount) &&
    canExportUsers;

  if (error) return <div>Error loading team.</div>;

  return (
    <Box>
      <Header>
        <HeaderTitle>Team</HeaderTitle>
        <FlexRow gap="s">
          {canBulkInvite && (
            <Button
              leftIconName="upload"
              styleVariant="secondary"
              size="small"
              data-testid="bulk-invite-button"
              onClick={() => history.push("/settings/bulk-user-invite")}
            >
              Bulk Invite
            </Button>
          )}
          <Button
            leftIconName="plus"
            styleVariant="primary"
            onClick={showNewUserModal}
            size="small"
            data-testid="new-user-button"
          >
            Add user
          </Button>
          {showExportUsers ?
            <ExportTeam />
          : null}
        </FlexRow>
      </Header>
      <TeamTable
        account={account}
        previousData={previousData?.usersAndInvites.data}
        currentData={data?.usersAndInvites.data ?? defaultData}
        loading={loading}
        manualPaginationConfig={{
          ...manualPaginationConfig,
          pageCount: data?.usersAndInvites.pageInfo.totalPages ?? -1,
        }}
        onUpdate={refetch}
        initialTableState={initialTableState}
        setTableStateInURL={setTableStateInURL}
      />
    </Box>
  );
};

const ExportTeam = () => {
  const { addErrorToast } = useStatusToasts();
  const { setModalProps } = useExportModal();

  const [exportUsersAndInvitesForAccount] =
    useExportUsersAndInvitesForAccountMutation({
      onCompleted: () => {
        setModalProps({
          title: "Export data",
          message:
            "Your team export is currently processing. Once your data is ready, we'll email a link to download your file.",
        });
      },
      onError: () => {
        addErrorToast(
          "There was an error with your team export. Please try again. If the problem persists, please email support@withforerunner.com",
        );
      },
    });

  return (
    <ExportButton
      onClickExport={async () => {
        await exportUsersAndInvitesForAccount();
      }}
      ariaLabel="Export team"
    />
  );
};

export default Team;
